import SendIcon from '@mui/icons-material/Send';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import type { TSubmitAnswer } from '@onesource/schemas';
import { ContentBlockType, LessonProgressStatus } from '@onesource/schemas';
import type { TQuizQuestion } from '@onesource/services';
import {
  useCompleteBlock,
  useGetQuiz,
  useGetUserAttempts,
  useStartAttempt,
  useSubmitAttempt,
  useUpdateProgress,
} from '@onesource/services';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdArrowBack as ArrowBackIcon, MdChevronLeft, MdChevronRight } from 'react-icons/md';

import { useResolvedBlock } from '#/features/community-hub/components/block-viewers/use-resolved-block';
import { LessonBreadcrumbs } from '#/features/community-hub/components/lesson-breadcrumbs';
import { QuestionRenderer } from '#/features/community-hub/components/quiz/question-renderer';
import { QuizTimer } from '#/features/community-hub/components/quiz/quiz-timer';
/**
 * Quiz Content Route
 * Path: /programs/{program}/quiz/{itemSlug}
 * Shows quiz for a lesson's quiz content block
 */
export const Route = createFileRoute('/_protected/programs/$programSlug/quiz/$itemSlug')({
  component: QuizContentRoute,
});

function QuizContentRoute() {
  const { itemSlug } = Route.useParams();
  const {
    programId,
    moduleId,
    lessonId,
    programSlug,
    moduleSlug,
    lessonSlug,
    lessonBasePath,
    lesson,
    module,
    findParentLesson,
    block: quizBlock,
    contentId,
    isLoading: isLoadingLesson,
    error: lessonError,
  } = useResolvedBlock(itemSlug, ContentBlockType.QUIZ);
  const navigate = useNavigate();

  const quizId = quizBlock?.quizId;

  // Fetch quiz data (only when we have the quizId)
  const { data: quiz, isLoading: isLoadingQuiz, error: quizError } = useGetQuiz(quizId || '');
  const { data: userAttempts } = useGetUserAttempts(quizId || '');

  // Mutations
  const startAttempt = useStartAttempt();
  const submitAttempt = useSubmitAttempt();

  // Local state
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, TSubmitAnswer>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showTimeUpDialog, setShowTimeUpDialog] = useState(false);
  const [attemptStarted, setAttemptStarted] = useState(false);

  // Get sorted questions
  const questions: TQuizQuestion[] = quiz?.questions ? [...quiz.questions].sort((a, b) => a.position - b.position) : [];
  const currentQuestion = questions[currentQuestionIndex];

  // Check if max attempts reached
  const attemptsUsed = userAttempts?.length || 0;
  const maxAttemptsReached = quiz?.maxAttempts && attemptsUsed >= quiz.maxAttempts;

  // Back to lesson URL
  const backToLessonUrl = lessonBasePath;

  const trackedRef = useRef(false);
  const { mutate: updateProgress } = useUpdateProgress(lessonId);
  const { mutate: completeBlock } = useCompleteBlock();
  useEffect(() => {
    if (!trackedRef.current && quizBlock) {
      trackedRef.current = true;
      updateProgress({ status: LessonProgressStatus.IN_PROGRESS, lastBlockId: quizBlock?.id });
    }
  }, [quizBlock, updateProgress]);

  // Initialize answers when quiz loads
  useEffect(() => {
    if (quiz && questions.length > 0 && Object.keys(answers).length === 0) {
      const initialAnswers: Record<string, TSubmitAnswer> = {};
      questions.forEach(q => {
        initialAnswers[q.id] = {
          questionId: q.id,
          selectedOptionIds: [],
          orderingSequence: [],
          textAnswer: '',
        };
      });
      setAnswers(initialAnswers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz, questions]);

  // Start attempt when quiz loads
  useEffect(() => {
    if (quiz && quizId && !attemptId && !maxAttemptsReached && !attemptStarted) {
      setAttemptStarted(true);
      startAttempt.mutate(quizId, {
        onSuccess: attempt => {
          setAttemptId(attempt.id);
        },
        onError: error => {
          console.error('Failed to start attempt:', error);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz, quizId, attemptId, maxAttemptsReached, attemptStarted]);

  // Handle answer change
  const handleAnswerChange = useCallback((answer: TSubmitAnswer) => {
    setAnswers(prev => ({
      ...prev,
      [answer.questionId]: answer,
    }));
  }, []);

  // Navigation
  const goToPrevious = () => setCurrentQuestionIndex(prev => Math.max(0, prev - 1));
  const goToNext = () => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1));

  // Check if question is answered
  const isQuestionAnswered = (questionId: string): boolean => {
    const answer = answers[questionId];
    if (!answer) {
      return false;
    }

    const question = questions.find(q => q.id === questionId);
    if (!question) {
      return false;
    }

    switch (question.questionType) {
      case 'multiple_choice':
      case 'multiple_select':
      case 'true_false': {
        return (answer.selectedOptionIds?.length || 0) > 0;
      }
      case 'short_answer': {
        return (answer.textAnswer?.trim().length || 0) > 0;
      }
      case 'ordering': {
        return (answer.orderingSequence?.length || 0) > 0;
      }
      default: {
        return false;
      }
    }
  };

  const answeredCount = questions.filter(q => isQuestionAnswered(q.id)).length;

  // Handle time up
  const handleTimeUp = useCallback(() => {
    setShowTimeUpDialog(true);
  }, []);

  // Submit quiz
  const handleSubmit = async () => {
    if (!attemptId) {
      return;
    }

    setIsSubmitting(true);
    setShowSubmitDialog(false);
    setShowTimeUpDialog(false);

    try {
      const answersArray = Object.values(answers);
      const result = await submitAttempt.mutateAsync({
        attemptId,
        data: { answers: answersArray },
      });

      // Submitting the quiz completes this block (backend verifies the attempt).
      completeBlock(contentId);

      // Navigate to results page under the lesson route
      navigate({ to: `${lessonBasePath}/results/${result.id}` });
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      setIsSubmitting(false);
    }
  };

  // Loading states
  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load lesson: {lessonError.message}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (!quizBlock) {
    return (
      <Box p={3}>
        <Alert severity="error">Quiz content block not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (!quizId) {
    return (
      <Box p={3}>
        <Alert severity="warning">No quiz associated with this content block</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (isLoadingQuiz) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (quizError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load quiz: {quizError.message}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  // Max attempts reached
  if (maxAttemptsReached) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have reached the maximum number of attempts ({quiz?.maxAttempts}) for this quiz.
        </Alert>
        <Typography variant="h5" gutterBottom>
          {quiz?.title}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          You have completed {attemptsUsed} attempt(s).
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  // Waiting for attempt to start
  if (!attemptId || startAttempt.isPending) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
        <CircularProgress />
        <Typography color="text.secondary">Starting quiz...</Typography>
      </Box>
    );
  }

  // No questions
  if (questions.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">This quiz has no questions yet.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  // Calculate progress percentage
  const progressPercent = Math.round(((currentQuestionIndex + 0) / questions.length) * 100);

  return (
    <>
      <LessonBreadcrumbs
        spaceId={programId}
        moduleId={moduleId}
        lessonId={lessonId}
        module={module}
        lesson={lesson}
        parentLesson={findParentLesson}
        currentLabel="Quiz"
        programSlug={programSlug}
        moduleSlug={moduleSlug}
        lessonSlug={lessonSlug}
      />

      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%)',
          p: 2.5,
        }}
      >
        <Box maxWidth="800px" mx="auto">
          {/* Progress Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, md: 3 } }}>
            <Typography sx={{ fontWeight: 500, minWidth: 40 }}>{progressPercent}%</Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(0,0,0,0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  bgcolor: '#ff4d6d',
                },
              }}
            />
            {/* Timer (if applicable) */}
            {quiz?.timeLimitSeconds && (
              <QuizTimer totalSeconds={quiz.timeLimitSeconds} onTimeUp={handleTimeUp} paused={isSubmitting} />
            )}
          </Box>

          {/* Question Title - Centered */}
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontFamily: 'Georgia, serif',
              fontWeight: 400,
              mb: { xs: 1.5, md: 2 },
            }}
          >
            Question {currentQuestionIndex + 1}
          </Typography>

          {/* Question Text - Centered */}
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: '1rem',
              lineHeight: 1.6,
              mb: { xs: 2, md: 4 },
              maxWidth: 700,
              mx: 'auto',
            }}
          >
            {currentQuestion.questionText}
          </Typography>

          {/* Question Options */}
          <Box sx={{ mb: 5 }}>
            <QuestionRenderer
              question={currentQuestion}
              answer={answers[currentQuestion.id] || { questionId: currentQuestion.id }}
              onAnswerChange={handleAnswerChange}
              disabled={isSubmitting}
            />
          </Box>

          {/* Navigation Buttons */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button
              startIcon={<MdChevronLeft size={20} />}
              onClick={goToPrevious}
              disabled={currentQuestionIndex === 0 || isSubmitting}
              sx={{
                bgcolor: '#ff4d6d',
                color: 'white',
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { bgcolor: '#e63956' },
                '&:disabled': { bgcolor: '#ccc', color: '#666' },
              }}
            >
              Previous
            </Button>

            {currentQuestionIndex < questions.length - 1 ? (
              <Button
                endIcon={<MdChevronRight size={20} />}
                onClick={goToNext}
                disabled={isSubmitting}
                sx={{
                  bgcolor: '#ff4d6d',
                  color: 'white',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { bgcolor: '#e63956' },
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                endIcon={<SendIcon />}
                onClick={() => setShowSubmitDialog(true)}
                disabled={isSubmitting}
                sx={{
                  bgcolor: '#4caf50',
                  color: 'white',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { bgcolor: '#388e3c' },
                }}
              >
                Submit Quiz
              </Button>
            )}
          </Box>
        </Box>

        {/* Submit Confirmation Dialog */}
        <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)}>
          <DialogTitle>Submit Quiz?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You have answered {answeredCount} of {questions.length} questions.{' '}
              {answeredCount < questions.length && (
                <Box component="span" sx={{ color: 'warning.main', display: 'block', mt: 1 }}>
                  Warning: You have {questions.length - answeredCount} unanswered question(s).
                </Box>
              )}
              Are you sure you want to submit your quiz?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setShowSubmitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Time Up Dialog */}
        <Dialog open={showTimeUpDialog} disableEscapeKeyDown>
          <DialogTitle>Time&apos;s Up!</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Your time has expired. Your quiz will be submitted automatically with your current answers.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSubmit} variant="contained" color="primary" autoFocus>
              View Results
            </Button>
          </DialogActions>
        </Dialog>

        {/* Submitting Overlay */}
        {isSubmitting && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Submitting your quiz...
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
}
